import { FaceFrownIcon } from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'

import DatableLogo from '@ui/assets/logos/datable-logo.svg'
import { DatableColoredIcon } from '@ui/components/icons'
import { SignupPageStatus } from '@ui/interfaces'
import {
  ConfirmEmail,
  ForgotForm,
  InvitedForm,
  LoginForm,
  ResetForm,
  SignupForm,
} from '@ui/pages/authentication/components'

import { SuccessForm } from './components/SuccessForm'

type LoginProps = {
  initialState?: SignupPageStatus
}

export type UpdatePageStatus = (status: SignupPageStatus, transitionFromSuccessCallback?: () => void) => void

export const Login = ({ initialState }: LoginProps) => {
  const [pageStatus, setPageStatus] = useState(initialState || SignupPageStatus.LOGIN)
  const transitionFromSuccessCallbackRef = useRef<(() => void) | null>(null)
  const [message, setMessage] = useState<string | undefined>(undefined)

  const updatePageStatus: UpdatePageStatus = (status, transitionFromSuccessCallback) => {
    setPageStatus(status)
    if (transitionFromSuccessCallback) {
      transitionFromSuccessCallbackRef.current = transitionFromSuccessCallback
    }
  }

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center">
      <div className="flex w-full flex-col items-center justify-center p-8 md:w-[37.5rem] md:p-0">
        <img src={DatableLogo} alt="Datable Logo" className="hidden h-14 w-auto md:block" />
        <DatableColoredIcon className="block h-14 w-auto md:hidden" />
        {pageStatus === SignupPageStatus.LOGIN && <LoginForm setPageStatus={updatePageStatus} />}
        {pageStatus === SignupPageStatus.SIGNUP && <SignupForm setPageStatus={updatePageStatus} />}
        {pageStatus === SignupPageStatus.DEMO && <SignupForm setPageStatus={updatePageStatus} isDemo={true} />}
        {pageStatus === SignupPageStatus.FORGOT && (
          <ForgotForm setPageStatus={updatePageStatus} setMessage={setMessage} />
        )}
        {pageStatus === SignupPageStatus.RESET && <ResetForm setPageStatus={updatePageStatus} />}
        {pageStatus === SignupPageStatus.INVITED && <InvitedForm setPageStatus={updatePageStatus} />}
        {pageStatus === SignupPageStatus.CONFIRM_EMAIL && (
          <ConfirmEmail setPageStatus={updatePageStatus} setMessage={setMessage} />
        )}
        {pageStatus === SignupPageStatus.CONFIRM_EMAIL_NOTIFICATION && (
          <div className="space-y-2 py-8 text-center">
            <h1 className="text-xl font-medium text-gray-800">Verify your email</h1>
            <p className="text-sm text-gray-600">
              To complete your registration and activate your account, please check your email for a verification link.
            </p>
          </div>
        )}
        {pageStatus === SignupPageStatus.SUCCESS && (
          <SuccessForm message={message} transitionFromSuccessCallback={transitionFromSuccessCallbackRef.current} />
        )}
        {pageStatus === SignupPageStatus.FAIL && (
          <div className="mx-auto flex items-center justify-center pt-12">
            <FaceFrownIcon className="mr-4 h-14 w-14 text-primary-600" />
            <h1 className="text-lg leading-tight tracking-tight">
              {message || 'An error occurred when attempting to send a login link'}
            </h1>
          </div>
        )}
      </div>
    </div>
  )
}
